/**
 * Shared recording cleanup utility
 * 
 * Centralizes the logic for stopping an active recording session:
 * audio capture, WebSocket connection, microphone, and Redux state.
 * 
 * Used by SessionDetailPage (unmount) and Header (navigation) to ensure
 * cleanup runs regardless of how the user navigates away from a session.
 */

import { audioService } from '../services/audioService';
import { websocketService } from '../services/websocketService';
import { resetRecording, audioStreamReleased } from '../store/recordingSlice';
import type { AppDispatch } from '../store/store';

/**
 * Clean up an active recording session.
 * 
 * Sequence:
 * 1. Stop audio capture (stops sending chunks to WebSocket)
 * 2. Send stop_session to backend (graceful — lets backend finalize)
 * 3. Disconnect WebSocket (close connection + prevent reconnect)
 * 4. Release microphone
 * 5. Reset Redux recording state
 * 
 * Safe to call even when no recording is active — each step is guarded.
 */
export function cleanupRecording(dispatch: AppDispatch): void {
  // 1. Stop audio capture (no-op if not recording)
  audioService.stopRecording().catch(() => {});

  // 2. Tell backend to stop gracefully (if WebSocket is connected)
  //    This triggers backend's handle_stop_session → finalize audio, post-process, etc.
  if (websocketService.isConnected()) {
    try {
      websocketService.stop();
    } catch {
      // Ignore — WebSocket may already be closing
    }
  }

  // 3. Disconnect WebSocket (close + cancel reconnect + clear state)
  websocketService.disconnect();

  // 4. Release microphone
  audioService.releaseMicrophone();

  // 5. Reset Redux recording state
  dispatch(resetRecording());
  dispatch(audioStreamReleased());
}
